<template>
  <div ref="LvStateDropDownSelectMultiLayer" class="lv-state-dropdown-select-multi-layer">
    <p v-if="title" class="lv-state-dropdown-select-multi-layer__title">
      {{ $t(title)
      }}<span v-if="required" class="lv-state-dropdown-select-multi-layer__required">*</span>
    </p>
    <ValidationProvider
      v-slot="{ errors }"
      class="lv-state-dropdown-select-multi-layer__content-wrap"
      :rules="rules"
    >
      <input type="hidden" :value="value" />
      <div
        v-click-outside="$_onDropdownClose"
        class="lv-state-dropdown-select-multi-layer__content lv-state-dropdown-select-multi-layer__content--outline"
        :class="{
          'lv-state-dropdown-select-multi-layer__content--disabled': disabled,
          'lv-state-dropdown-select-multi-layer__content--error': errors.length,
        }"
      >
        <button class="lv-state-dropdown-select-multi-layer__trigger" @click="$_onClick()">
          <p
            class="lv-state-dropdown-select-multi-layer__text"
            :class="{
              'lv-state-dropdown-select-multi-layer__text--active': value && value.length > 0,
            }"
          >
            {{ _showText }}
          </p>
          <span
            v-if="_selectAllItemsIcon === 'icon-indeterminate-check-box'"
            class="lv-state-dropdown-select-multi-layer__count"
            >{{ value.length }}</span
          >
        </button>
        <i
          class="icon icon-chevron-up lv-state-dropdown-select-multi-layer__icon lv-state-dropdown-select-multi-layer__icon--trailing lv-state-dropdown-select-multi-layer__icon--outline"
          :class="{
            'lv-state-dropdown-select-multi-layer__icon--show': isShow.menu,
          }"
        />
        <transition mode="out-in">
          <div
            v-if="isShow.menu"
            class="lv-state-dropdown-select-multi-layer__dropdown-menu"
            :class="{
              'lv-state-dropdown-select-multi-layer__dropdown-menu--drop-up': isDropUp,
            }"
          >
            <div class="lv-state-dropdown-select-multi-layer__dropdown-scrollable-content">
              <div>
                <SearchInput
                  v-if="searchable"
                  v-model="search"
                  textLabel="搜尋"
                  :placeholder="$t('搜尋')"
                  @enter="$emit('search', search)"
                />
                <button
                  class="lv-state-dropdown-select-multi-layer__select-all-button"
                  @click="$_onSelectAll()"
                >
                  <i
                    class="lv-state-dropdown-select-multi-layer__select-all-icon"
                    :class="_selectAllItemsIcon"
                  />
                  {{ $t(allSelectText) }}
                </button>
              </div>
              <DotLoading v-if="isLoading" />
              <perfect-scrollbar
                v-else
                class="lv-state-dropdown-select-multi-layer__dropdown-scrollable-content-list ps"
              >
                <LvStateMultiLayer
                  :value="value"
                  :items="items"
                  :layerKey="layerKey"
                  @input="$emit('input', $event)"
                />
              </perfect-scrollbar>
            </div>
          </div>
        </transition>
      </div>
      <div
        v-if="errors && errors.length > 0"
        class="lv-state-dropdown-select-multi-layer__invalid-text"
      >
        <i class="icon-warning lv-state-dropdown-select-multi-layer__invalid-icon" />
        <p
          v-for="(text, textIndex) in errors"
          :key="textIndex"
          class="lv-state-dropdown-select-multi-layer__invalid-text-item"
        >
          {{ $t(text) }}
        </p>
      </div>
      <WsTextChip001
        v-if="remindText"
        gray
        small
        thin
        :leadingIcon="'icon-warning'"
        :textLabel="remindText"
        class="mt-12"
      />
    </ValidationProvider>
  </div>
</template>

<script>
import ClickOutside from 'vue-click-outside';
import { ValidationProvider } from 'vee-validate';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';
import H_common from '@/helpers/common';

extend('required', {
  ...required,
  message: '此項目為必填',
});
export default {
  name: 'LvStateDropdownSelectMultiLayer',
  directives: {
    ClickOutside,
  },
  components: {
    ValidationProvider,
  },
  props: {
    items: {
      type: Array,
      default() {
        return [];
      },
    },
    value: {
      type: Array,
      default() {
        return [];
      },
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    rules: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    allSelectText: {
      type: String,
      default: '全部',
    },
    textKey: {
      type: String,
      default: 'name',
    },
    layerKey: {
      type: String,
      default: 'items',
    },
    remindText: {
      type: String,
    },
    scroll: {
      type: Number,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isShow: {
        menu: false,
      },
      isDropUp: false,
      dropdownHeight: 0,
      dropdownTop: 0,
      search: '',
    };
  },
  computed: {
    _value() {
      if (this.value) {
        return this.value;
      } else {
        return [];
      }
    },
    _showText() {
      const allItemsList = this.$_getAllItemsList(this.items);
      if (this._value.length === allItemsList.length && allItemsList.length) {
        if (this.allSelectText) {
          return this.$t(this.allSelectText);
        } else {
          return this.$t('全部');
        }
      } else if (this._value.length == 0) {
        return this.$t(this.placeholder);
      } else {
        const selectedItems = [];
        allItemsList.forEach(item => {
          if (this._value.includes(item.id)) {
            selectedItems.push(this.$t(item[`${this.textKey}`]));
          }
        });
        return selectedItems.join('、');
      }
    },
    _selectAllItemsIcon() {
      const allItemsList = this.$_getAllItemsList(this.items);
      if (this._value.length < allItemsList.length && this._value.length > 0) {
        return 'icon-indeterminate-check-box';
      } else if (this._value.length === 0) {
        return 'icon-check-box-outline-blank';
      }
      return 'icon-check-box';
    },
  },
  watch: {
    scroll() {
      this.$_getDropdownHeight();
      this.$_isDropUp();
    },
  },
  created() {
    window.addEventListener('resize', this.$_getDropdownHeight);
    window.addEventListener('resize', this.$_isDropUp);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.$_getDropdownHeight);
    window.removeEventListener('resize', this.$_isDropUp);
  },
  mounted() {
    this.$_getDropdownHeight();
    this.$_isDropUp();
  },
  methods: {
    $_getAllItemsList(tarList) {
      let result = [];
      tarList.forEach(item => {
        result.push(item);
        if (item[`${this.layerKey}`].length) {
          result = [...result, ...this.$_getAllItemsList(item[`${this.layerKey}`])];
        }
      });
      return result;
    },
    $_onClick() {
      if (this.disabled) {
        return;
      }
      if (this.isShow.menu === false) {
        this.$emit('click');
      }
      this.$_onToggleDropdown();
    },
    $_onToggleDropdown() {
      this.isShow.menu = !this.isShow.menu;
      this.$_clearData();
    },
    $_onDropdownClose() {
      this.isShow.menu = false;
      this.$_clearData();
    },
    $_onSelectAll() {
      let allItemsIdList = [];
      const allItemsList = this.$_getAllItemsList(this.items);
      const availableItemList = allItemsList.filter(item => !item.disable);
      if (this._value.length < availableItemList.length) {
        allItemsIdList = availableItemList.map(item => item.id);
      }
      this.$emit('input', allItemsIdList);
      this.$emit('click');
    },
    $_getDropdownHeight() {
      const { height, y } = H_common.getComponentHeightAndY(
        this.$refs.LvStateDropDownSelectMultiLayer,
      );
      this.dropdownHeight = height;
      this.dropdownTop = y;
    },
    $_isDropUp() {
      this.isDropUp = H_common.isDropUp(this.dropdownHeight, this.dropdownTop, 450, 450);
    },
    $_clearData() {
      this.search = '';
    },
  },
};
</script>
